// frontend/pages/Settings.jsx
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { apiFetch } from '../api.js'
import SettingsGeneral from './settings/SettingsGeneral.jsx'
import SettingsModules from './settings/SettingsModules.jsx'
import SettingsEmail from './settings/SettingsEmail.jsx'
import SettingsAutomations from './settings/SettingsAutomations.jsx'
import SettingsInvoice from './settings/SettingsInvoice.jsx'
import SettingsSecurity from './settings/SettingsSecurity.jsx'
import SettingsLoyalty from './settings/SettingsLoyalty.jsx'
import SettingsHR from './settings/SettingsHR.jsx'

/**
 * Settings page (pro + safe)
 *
 * RULES (front-side guard rails; backend must enforce too):
 * - superadmin: peut tout gérer (users + roles + toggle + delete + reset pwd)
 * - admin:
 *   - ne peut PAS agir sur un autre admin
 *   - ne peut PAS agir sur auditor
 *   - ne peut PAS agir sur superadmin
 *   - ne peut PAS modifier ses propres rôles / statut / suppression
 *   - peut uniquement changer SON mot de passe (self reset)
 *
 * USERS API used:
 * - GET    /api/auth/me
 * - GET    /api/auth/users
 * - POST   /api/auth/register
 * - POST   /api/auth/users/:id/force-password-change
 * - PATCH  /api/auth/users/:id/toggle
 * - DELETE /api/auth/users/:id
 * - PATCH  /api/auth/users/:id/roles
 *
 * INVOICE API used:
 * - GET  /api/invoice-template
 * - PUT  /api/invoice-template
 * - POST /api/invoice-template/logo
 * - POST /api/invoice-template/custom-template
 * - POST /api/invoice-template/custom-template/toggle
 * - DELETE /api/invoice-template/custom-template
 */
export default function Settings() {
  // ---------------- NAV INTERNE ----------------
  const location = useLocation()
  const allowedTabs = ['general', 'users', 'invoice', 'modules', 'email', 'automations', 'security', 'loyalty', 'hr']
  const initialTab = useMemo(() => {
    const params = new URLSearchParams(location.search || '')
    const fromQuery = String(params.get('tab') || '').trim()
    return allowedTabs.includes(fromQuery) ? fromQuery : 'general'
  }, [location.search])
  const [tab, setTab] = useState(initialTab)

  useEffect(() => {
    setTab(initialTab)
  }, [initialTab])

  // ---------------- FLASH GLOBAL ----------------
  const [flash, setFlash] = useState(null)
  useEffect(() => {
    if (!flash) return
    const t = setTimeout(() => setFlash(null), 4500)
    return () => clearTimeout(t)
  }, [flash])

  // ======================================================
  // ================== HELPERS UI/ROLES ==================
  // ======================================================

  const ROLE_RANK = { sales: 10, ops: 20, auditor: 30, admin: 90, superadmin: 100 }
  const ALL_ROLES = ['sales', 'ops', 'auditor', 'admin', 'superadmin']

  function normalizeRoles(v) {
    if (Array.isArray(v)) return v.filter(Boolean).map(String)
    if (typeof v === 'string') {
      const s = v.trim()
      if (!s) return []
      if (s.startsWith('[') && s.endsWith(']')) {
        try {
          const parsed = JSON.parse(s)
          if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String)
        } catch {}
      }
      if (s.includes(',')) return s.split(',').map(x => x.trim()).filter(Boolean)
      return [s]
    }
    return []
  }

  function hasAnyRole(userRoles, required) {
    const ur = normalizeRoles(userRoles)
    const rr = Array.isArray(required) ? required : []
    return ur.some(r => rr.includes(r))
  }

  function maxRank(roles = []) {
    const rr = normalizeRoles(roles)
    return rr.reduce((m, r) => Math.max(m, ROLE_RANK[r] || 0), 0)
  }

  function isSuper(viewerRoles) {
    return hasAnyRole(viewerRoles, ['superadmin'])
  }

  function isAdmin(viewerRoles) {
    return hasAnyRole(viewerRoles, ['admin'])
  }

  function isAuditor(roles) {
    return hasAnyRole(roles, ['auditor'])
  }

  function isTargetAdmin(roles) {
    return hasAnyRole(roles, ['admin'])
  }

  function isTargetSuper(roles) {
    return hasAnyRole(roles, ['superadmin'])
  }

  function canSeeRole(viewerRoles, role) {
    // superadmin visible uniquement par superadmin
    if (role === 'superadmin' && !isSuper(viewerRoles)) return false
    const viewerRank = maxRank(viewerRoles)
    const roleRank = ROLE_RANK[role] || 0
    return roleRank <= viewerRank
  }

  function canTouchTarget(viewerRoles, targetRoles) {
    // superadmin peut tout faire
    if (isSuper(viewerRoles)) return true

    // ADMIN: ne touche pas auditor, admin, superadmin
    if (isAdmin(viewerRoles)) {
      if (isAuditor(targetRoles)) return false
      if (isTargetAdmin(targetRoles)) return false
      if (isTargetSuper(targetRoles)) return false
      // + règle rank stricte (au-dessus seulement)
      return maxRank(viewerRoles) > maxRank(targetRoles)
    }

    return false
  }

  function canAssignRole(viewerRoles, role) {
    // superadmin peut assigner tout
    if (isSuper(viewerRoles)) return true

    // admin: seulement sales/ops
    if (isAdmin(viewerRoles)) return role === 'sales' || role === 'ops'

    return false
  }

  function computeRowPerms(u) {
    const viewer = viewerRoles
    const targetRoles = normalizeRoles(u?.roles)
    const self = me?.id && String(me.id) === String(u?.id)

    // sécurité mot de passe:
    // - superadmin: cible non-self
    // - admin: cibles gérables non-self
    const canResetPwd =
      !self &&
      (isSuper(viewer) ||
        (isAdmin(viewer) &&
          !isAuditor(targetRoles) &&
          !isTargetAdmin(targetRoles) &&
          !isTargetSuper(targetRoles) &&
          maxRank(viewer) > maxRank(targetRoles)))

    // manage actions (roles/toggle/delete):
    // - superadmin: tout (y compris self si tu veux, mais on garde self lock côté front)
    // - admin: seulement sur users "en dessous" ET jamais auditor/admin/superadmin ET pas self
    const canManage =
      !self &&
      (isSuper(viewer) ||
        (isAdmin(viewer) &&
          !isAuditor(targetRoles) &&
          !isTargetAdmin(targetRoles) &&
          !isTargetSuper(targetRoles) &&
          maxRank(viewer) > maxRank(targetRoles)))

    return {
      isSelf: self,
      targetRoles,
      canResetPwd,
      canManage, // roles/toggle/delete
    }
  }

  // ======================================================
  // =============== 1. GESTION UTILISATEURS ==============
  // ======================================================

  const [loadingUsers, setLoadingUsers] = useState(true)
  const [users, setUsers] = useState([])

  // /api/auth/me
  const [me, setMe] = useState(null)
  const viewerRoles = useMemo(() => normalizeRoles(me?.roles), [me])

  const canManageUsers = useMemo(() => {
    return !!me && (isAdmin(me.roles) || isSuper(me.roles))
  }, [me])

  // Form création user
  const [newUsername, setNewUsername] = useState('')
  const [newFullName, setNewFullName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Rôles cochables creation
  const [roleSales, setRoleSales] = useState(false)
  const [roleOps, setRoleOps] = useState(false)
  const [roleAuditor, setRoleAuditor] = useState(false)
  const [roleAdmin, setRoleAdmin] = useState(false)

  // Modal rôles (safe: PATCH /roles)
  const [rolesModalOpen, setRolesModalOpen] = useState(false)
  const [rolesTarget, setRolesTarget] = useState(null)
  const [rolesDraft, setRolesDraft] = useState([])
  const [rolesSaving, setRolesSaving] = useState(false)

  // password strength
  const [passwordStrength, setPasswordStrength] = useState(null)
  useEffect(() => {
    if (!newPassword) {
      setPasswordStrength(null)
      return
    }

    let strength = 'Faible'
    let color = 'text-red-600'

    if (newPassword.length >= 8) {
      strength = 'Moyen'
      color = 'text-yellow-600'
    }
    if (newPassword.length >= 8 && /[A-Z]/.test(newPassword) && /[0-9]/.test(newPassword)) {
      strength = 'Fort'
      color = 'text-green-600'
    }
    if (
      newPassword.length >= 12 &&
      /[A-Z]/.test(newPassword) &&
      /[0-9]/.test(newPassword) &&
      /[^A-Za-z0-9]/.test(newPassword)
    ) {
      strength = 'Très fort'
      color = 'text-green-700'
    }

    setPasswordStrength({ text: strength, color })
  }, [newPassword])

  async function loadMe() {
    try {
      const data = await apiFetch('/api/auth/me')
      if (data && typeof data === 'object') {
        setMe({ ...data, roles: normalizeRoles(data.roles) })
      } else {
        setMe(null)
      }
    } catch {
      setMe(null)
    }
  }

  async function loadUsers() {
    try {
      setLoadingUsers(true)
      const data = await apiFetch('/api/auth/users')
      if (!Array.isArray(data)) throw new Error('Réponse inattendue du serveur')
      setUsers(
        data.map(u => ({
          ...u,
          roles: normalizeRoles(u.roles),
        }))
      )
    } catch (err) {
      console.error('Erreur chargement utilisateurs:', err)
      setFlash('❌ Erreur chargement utilisateurs: ' + (err?.message || 'unknown'))
      setUsers([])
    } finally {
      setLoadingUsers(false)
    }
  }

  function validateUserForm() {
    if (!newUsername.trim()) {
      setFlash("❌ Le nom d'utilisateur est requis")
      return false
    }
    if (!newPassword) {
      setFlash('❌ Le mot de passe est requis')
      return false
    }
    if (newPassword !== confirmPassword) {
      setFlash('❌ Les mots de passe ne correspondent pas')
      return false
    }
    if (newPassword.length < 8) {
      setFlash('❌ Le mot de passe doit contenir au moins 8 caractères')
      return false
    }

    // admin: seulement sales/ops
    const allowedCreate = isSuper(viewerRoles)
      ? ['sales', 'ops', 'auditor', 'admin']
      : ['sales', 'ops']

    const picked = []
    if (roleSales) picked.push('sales')
    if (roleOps) picked.push('ops')
    if (roleAuditor) picked.push('auditor')
    if (roleAdmin) picked.push('admin')

    const effectivePicked = picked.filter(r => allowedCreate.includes(r))

    if (!effectivePicked.length) {
      setFlash('❌ Au moins un rôle doit être sélectionné (autorisé pour ton niveau)')
      return false
    }

    return true
  }

  async function handleCreateUser(e) {
    e.preventDefault()
    setFlash(null)
    if (!validateUserForm()) return

    const allowedCreate = isSuper(viewerRoles)
      ? ['sales', 'ops', 'auditor', 'admin']
      : ['sales', 'ops']

    const rolesToSend = []
    if (roleSales) rolesToSend.push('sales')
    if (roleOps) rolesToSend.push('ops')
    if (roleAuditor) rolesToSend.push('auditor')
    if (roleAdmin) rolesToSend.push('admin')

    const safeRoles = rolesToSend.filter(r => allowedCreate.includes(r))

    try {
      await apiFetch('/api/auth/register', {
        method: 'POST',
        body: {
          username: newUsername.trim(),
          password: newPassword,
          full_name: newFullName.trim(),
          email: newEmail.trim(),
          roles: safeRoles,
        },
      })

      setFlash('✅ Utilisateur créé avec succès')

      setNewUsername('')
      setNewFullName('')
      setNewEmail('')
      setNewPassword('')
      setConfirmPassword('')
      setRoleSales(false)
      setRoleOps(false)
      setRoleAuditor(false)
      setRoleAdmin(false)

      loadUsers()
    } catch (err) {
      console.error('Erreur création utilisateur:', err)
      const errCode = String(err?.data?.error || err?.message || '').toLowerCase()
      if (errCode === 'entitlement_limit_reached') {
        const { current, max, plan_code } = err?.data || {}
        setFlash(
          `❌ Limite utilisateurs atteinte (${current ?? '?'}/${max ?? '?'} sur plan ${plan_code ?? '?'}). ` +
          `Passez à un plan supérieur dans l'onglet Abonnement.`
        )
      } else {
        setFlash('❌ Erreur création utilisateur : ' + (err?.message || 'unknown'))
      }
    }
  }

  async function handleForcePasswordChange(userId, username) {
    // eslint-disable-next-line no-restricted-globals
    const ok = confirm(`Forcer "${username}" à changer son mot de passe au prochain login ?`)
    if (!ok) return
    try {
      await apiFetch(`/api/auth/users/${userId}/force-password-change`, {
        method: 'POST',
      })
      setFlash('✅ Changement de mot de passe forcé')
      loadUsers()
    } catch (err) {
      console.error('Erreur force password change:', err)
      setFlash('❌ Erreur sécurité utilisateur: ' + (err?.message || 'unknown'))
    }
  }

  async function handleToggleUser(userId, username, currentStatus) {
    const action = currentStatus ? 'désactiver' : 'réactiver'
    // eslint-disable-next-line no-restricted-globals
    const ok = confirm(`Êtes-vous sûr de vouloir ${action} l'utilisateur "${username}" ?`)
    if (!ok) return

    try {
      await apiFetch(`/api/auth/users/${userId}/toggle`, { method: 'PATCH' })
      setFlash(`✅ Utilisateur ${action} avec succès`)
      loadUsers()
    } catch (err) {
      console.error(`Erreur ${action} utilisateur:`, err)
      setFlash(`❌ Erreur lors de la ${action}: ` + (err?.message || 'unknown'))
    }
  }

  async function handleDeleteUser(userId, username) {
    if (me?.id && String(me.id) === String(userId)) {
      setFlash('❌ Impossible de supprimer ton propre compte')
      return
    }
    // eslint-disable-next-line no-restricted-globals
    const ok = confirm(`Supprimer définitivement l'utilisateur "${username}" ?`)
    if (!ok) return

    try {
      await apiFetch(`/api/auth/users/${userId}`, { method: 'DELETE' })
      setFlash('✅ Utilisateur supprimé')
      loadUsers()
    } catch (err) {
      console.error('Erreur suppression utilisateur:', err)
      setFlash('❌ Erreur suppression utilisateur: ' + (err?.message || 'unknown'))
    }
  }

  function openRolesModal(u) {
    const targetRoles = normalizeRoles(u.roles)
    const self = me?.id && String(me.id) === String(u.id)

    if (self) {
      setFlash("❌ Pour ta sécurité: modification de tes rôles désactivée ici")
      return
    }

    if (!canTouchTarget(viewerRoles, targetRoles)) {
      setFlash("❌ Action refusée: niveau d'accès insuffisant")
      return
    }

    // visible + assignable roles
    const visibleAssignable = ALL_ROLES.filter(r => canSeeRole(viewerRoles, r) && canAssignRole(viewerRoles, r))
    const initial = normalizeRoles(u.roles).filter(r => visibleAssignable.includes(r))

    setRolesTarget(u)
    setRolesDraft(initial)
    setRolesModalOpen(true)
  }

  function toggleRoleDraft(role) {
    setRolesDraft(prev => (prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]))
  }

  async function saveRoles() {
    if (!rolesTarget) return

    const targetRoles = normalizeRoles(rolesTarget.roles)

    if (!canTouchTarget(viewerRoles, targetRoles)) {
      setFlash("❌ Action refusée: niveau d'accès insuffisant")
      return
    }

    const assignable = ALL_ROLES.filter(r => canSeeRole(viewerRoles, r) && canAssignRole(viewerRoles, r))
    const nextRoles = rolesDraft.map(String).filter(r => assignable.includes(r))

    if (!nextRoles.length) {
      setFlash('❌ Sélectionne au moins un rôle valide')
      return
    }

    try {
      setRolesSaving(true)
      await apiFetch(`/api/auth/users/${rolesTarget.id}/roles`, {
        method: 'PATCH',
        body: { roles: nextRoles },
      })
      setFlash('✅ Rôles mis à jour')
      setRolesModalOpen(false)
      setRolesTarget(null)
      setRolesDraft([])
      loadUsers()
    } catch (err) {
      console.error('Erreur update roles:', err)
      setFlash('❌ Erreur rôles: ' + (err?.message || 'unknown'))
    } finally {
      setRolesSaving(false)
    }
  }

  // Charger (me + users + template) au montage
  useEffect(() => {
    loadMe()
    loadUsers()
    loadTemplate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ======================================================
  // ============ 2. FORMAT FACTURE / TEMPLATE ============
  // ======================================================

  const [tplLoading, setTplLoading] = useState(true)
  const [tplSaving, setTplSaving] = useState(false)

  // champs config facture
  const [companyName, setCompanyName] = useState('')
  const [companyAddress, setCompanyAddress] = useState('')
  const [companyContacts, setCompanyContacts] = useState('')
  const [companyLegal, setCompanyLegal] = useState('')
  const [footerNote, setFooterNote] = useState('')
  const [accentColor, setAccentColor] = useState('#000000')
  const [showLogo, setShowLogo] = useState(true)
  const [pageFormat, setPageFormat] = useState('A4')
  const [logoPath, setLogoPath] = useState(null)

  // modèle personnalisé
  const [useCustomTemplate, setUseCustomTemplate] = useState(false)
  const [customTemplateType, setCustomTemplateType] = useState(null)

  // mapping champs custom (JSON texte côté UI)
  const [customFieldMapText, setCustomFieldMapText] = useState('{}')

  async function loadTemplate() {
    try {
      setTplLoading(true)
      const data = await apiFetch('/api/invoice-template')
      if (data) {
        setCompanyName(data.company_name ?? '')
        setCompanyAddress(data.company_address ?? '')
        setCompanyContacts(data.company_contacts ?? '')
        setCompanyLegal(data.company_legal ?? '')
        setFooterNote(data.footer_note ?? '')
        setAccentColor(data.accent_color ?? '#000000')
        setShowLogo(data.show_logo !== false)
        setPageFormat(data.page_format ?? 'A4')
        setLogoPath(data.logo_path ?? null)

        setUseCustomTemplate(data.use_custom_template ?? false)
        setCustomTemplateType(data.custom_template_type ?? null)

        if (data.custom_field_map && typeof data.custom_field_map === 'object') {
          try {
            setCustomFieldMapText(JSON.stringify(data.custom_field_map, null, 2))
          } catch {
            setCustomFieldMapText('{}')
          }
        } else {
          setCustomFieldMapText('{}')
        }
      }
    } catch (err) {
      console.error('Erreur chargement template facture:', err)
      setFlash('❌ Erreur chargement facture: ' + (err?.message || 'unknown'))
    } finally {
      setTplLoading(false)
    }
  }

  async function handleSaveTemplate(e) {
    e.preventDefault()
    setTplSaving(true)
    setFlash(null)

    let parsedMap = {}
    try {
      if (customFieldMapText.trim()) parsedMap = JSON.parse(customFieldMapText)
    } catch {
      setTplSaving(false)
      setFlash('❌ custom_field_map invalide (JSON incorrect)')
      return
    }

    try {
      const body = {
        company_name: companyName,
        company_address: companyAddress,
        company_contacts: companyContacts,
        company_legal: companyLegal,
        footer_note: footerNote,
        accent_color: accentColor,
        show_logo: showLogo,
        page_format: pageFormat,
        custom_field_map: parsedMap,
      }

      const updated = await apiFetch('/api/invoice-template', {
        method: 'PUT',
        body,
      })

      setFlash('✅ Modèle de facture enregistré')

      if (updated) {
        setCompanyName(updated.company_name ?? '')
        setCompanyAddress(updated.company_address ?? '')
        setCompanyContacts(updated.company_contacts ?? '')
        setCompanyLegal(updated.company_legal ?? '')
        setFooterNote(updated.footer_note ?? '')
        setAccentColor(updated.accent_color ?? '#000000')
        setShowLogo(updated.show_logo !== false)
        setPageFormat(updated.page_format ?? 'A4')
        setLogoPath(updated.logo_path ?? null)

        if (updated.custom_field_map && typeof updated.custom_field_map === 'object') {
          try {
            setCustomFieldMapText(JSON.stringify(updated.custom_field_map, null, 2))
          } catch {}
        }
      }
    } catch (err) {
      console.error('Erreur sauvegarde template:', err)
      setFlash('❌ Erreur sauvegarde facture: ' + (err?.message || 'unknown'))
    } finally {
      setTplSaving(false)
    }
  }

  async function handleUploadLogo(e) {
    const file = e.target.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('logo', file)

    try {
      const resp = await fetch('/api/invoice-template/logo', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      if (!resp.ok) {
        const txt = await resp.text()
        throw new Error(txt || 'upload failed')
      }

      const data = await resp.json()
      const newLogo = data.logo_path || data.template?.logo_path || null

      if (newLogo) {
        setLogoPath(newLogo)
        setShowLogo(true)
        setFlash('✅ Logo mis à jour')
      } else {
        setFlash('ℹ️ Upload OK (logo_path non retourné)')
      }
    } catch (err) {
      console.error('Upload logo error:', err)
      setFlash('❌ Erreur upload logo: ' + (err?.message || 'unknown'))
    } finally {
      e.target.value = ''
    }
  }

  async function handleUploadCustomTemplate(e) {
    const file = e.target.files?.[0]
    if (!file) return

    let parsedMap = {}
    try {
      if (customFieldMapText.trim()) parsedMap = JSON.parse(customFieldMapText)
    } catch {
      setFlash('❌ custom_field_map invalide (JSON incorrect)')
      e.target.value = ''
      return
    }

    const formData = new FormData()
    formData.append('template', file)
    formData.append('custom_field_map', JSON.stringify(parsedMap))

    try {
      const resp = await fetch('/api/invoice-template/custom-template', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      if (!resp.ok) {
        const txt = await resp.text()
        throw new Error(txt || 'upload failed')
      }

      const data = await resp.json()

      setUseCustomTemplate(true)
      setCustomTemplateType(
        data.template?.custom_template_type || data.template?.custom_template_type || 'docx'
      )

      setFlash('✅ Modèle personnalisé importé et activé')
      loadTemplate()
    } catch (err) {
      console.error('Upload template error:', err)
      setFlash('❌ Erreur upload modèle: ' + (err?.message || 'unknown'))
    } finally {
      e.target.value = ''
    }
  }

  async function handleToggleCustomTemplate(enabled) {
    try {
      await apiFetch('/api/invoice-template/custom-template/toggle', {
        method: 'POST',
        body: { enabled },
      })

      setUseCustomTemplate(enabled)
      setFlash(enabled ? '✅ Modèle personnalisé ACTIVÉ' : 'ℹ️ Modèle personnalisé désactivé')
      loadTemplate()
    } catch (err) {
      console.error('Toggle custom template error:', err)
      setFlash('❌ Erreur activation modèle: ' + (err?.message || 'unknown'))
    }
  }

  async function handleDeleteCustomTemplate() {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Êtes-vous sûr de vouloir désactiver/supprimer le modèle personnalisé ?')) return

    try {
      await apiFetch('/api/invoice-template/custom-template', { method: 'DELETE' })
      setUseCustomTemplate(false)
      setCustomTemplateType(null)
      setFlash('✅ Modèle personnalisé désactivé')
      loadTemplate()
    } catch (err) {
      console.error('Delete custom template error:', err)
      setFlash('❌ Erreur suppression modèle: ' + (err?.message || 'unknown'))
    }
  }

  // ======================================================
  // =========== RENDUS SEPARÉS PAR ONGLET ================
  // ======================================================

  function Pill({ children }) {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-900">
        {children}
      </span>
    )
  }

  function renderUsersTab() {
    if (!canManageUsers) {
      return (
        <section className="bg-surface-card rounded-lg shadow-sm border p-6">
          <div className="text-sm text-content-primary">Accès refusé. (Rôle admin requis)</div>
        </section>
      )
    }

    const allowCreateAuditor = isSuper(viewerRoles)
    const allowCreateAdmin = isSuper(viewerRoles)

    return (
      <>
        {/* LISTE UTILISATEURS */}
        <section className="bg-surface-card rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-content-primary">Utilisateurs existants</h2>
            <p className="text-sm text-content-secondary mt-1">{users.length} utilisateur(s) configuré(s)</p>
          </div>

          {loadingUsers ? (
            <div className="p-8 text-center">
              <div className="text-content-secondary">Chargement en cours...</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-primary border-b">
                  <tr>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-content-primary">
                      Utilisateur
                    </th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-content-primary">
                      Nom complet
                    </th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-content-primary">
                      Email
                    </th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-content-primary">
                      Rôles
                    </th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-content-primary">
                      Statut
                    </th>
                    <th className="py-3 px-4 text-left text-sm font-semibold text-content-primary">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-border">
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-8 px-4 text-center text-content-secondary">
                        Aucun utilisateur configuré.
                      </td>
                    </tr>
                  )}

                  {users.map(u => {
                    const { isSelf, canResetPwd, canManage } = computeRowPerms(u)

                    return (
                      <tr key={u.id} className="hover:bg-surface-primary align-top">
                        <td className="py-3 px-4">
                          <div className="font-medium text-content-primary">{u.username}</div>
                          <div className="text-[11px] text-content-secondary">ID: {u.id}</div>
                          {isSelf && (
                            <div className="mt-1 text-[11px] text-content-secondary">
                              (ton compte)
                            </div>
                          )}
                        </td>

                        <td className="py-3 px-4 text-content-primary">{u.full_name || '—'}</td>

                        <td className="py-3 px-4 text-content-primary">{u.email || '—'}</td>

                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {normalizeRoles(u.roles).length ? (
                              normalizeRoles(u.roles).map(role => <Pill key={role}>{role}</Pill>)
                            ) : (
                              <span className="text-xs text-content-secondary">—</span>
                            )}
                          </div>
                        </td>

                        <td className="py-3 px-4">
                          <div className="flex flex-wrap items-center gap-1">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                u.is_active !== false
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {u.is_active !== false ? 'Actif' : 'Inactif'}
                            </span>
                            {u.force_password_change ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                MDP à changer
                              </span>
                            ) : null}
                          </div>
                        </td>

                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => openRolesModal(u)}
                              disabled={!canManage}
                              className={`text-xs px-3 py-1.5 rounded transition-colors ${
                                canManage
                                  ? 'bg-slate-900 hover:bg-slate-950 text-white'
                                  : 'bg-gray-200 text-content-secondary cursor-not-allowed'
                              }`}
                              title={
                                isSelf
                                  ? 'Non autorisé sur ton compte'
                                  : !canManage
                                    ? 'Niveau insuffisant'
                                    : 'Modifier rôles'
                              }
                              type="button"
                            >
                              Modifier rôles
                            </button>

                            <button
                              onClick={() => handleForcePasswordChange(u.id, u.username)}
                              disabled={!canResetPwd}
                              className={`text-xs px-3 py-1.5 rounded transition-colors ${
                                canResetPwd
                                  ? 'bg-slate-900 hover:bg-slate-950 text-white'
                                  : 'bg-gray-200 text-content-secondary cursor-not-allowed'
                              }`}
                              title={
                                canResetPwd
                                  ? (isSelf ? 'Action non disponible sur ton compte' : 'Forcer changement MDP')
                                  : "Niveau insuffisant"
                              }
                              type="button"
                            >
                              Forcer MDP
                            </button>

                            <button
                              onClick={() =>
                                handleToggleUser(u.id, u.username, u.is_active !== false)
                              }
                              disabled={!canManage}
                              className={`text-xs px-3 py-1.5 rounded transition-colors ${
                                canManage
                                  ? u.is_active !== false
                                    ? 'bg-red-600 hover:bg-red-700 text-white'
                                    : 'bg-green-600 hover:bg-green-700 text-white'
                                  : 'bg-gray-200 text-content-secondary cursor-not-allowed'
                              }`}
                              type="button"
                            >
                              {u.is_active !== false ? 'Désactiver' : 'Activer'}
                            </button>

                            <button
                              onClick={() => handleDeleteUser(u.id, u.username)}
                              disabled={!canManage}
                              className={`text-xs px-3 py-1.5 rounded transition-colors ${
                                canManage
                                  ? 'bg-red-700 hover:bg-red-800 text-white'
                                  : 'bg-gray-200 text-content-secondary cursor-not-allowed'
                              }`}
                              title={isSelf ? 'Impossible (toi-même)' : ''}
                              type="button"
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* CREATION UTILISATEUR */}
        <section className="bg-surface-card rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-content-primary">Créer un nouvel utilisateur</h2>
            {!isSuper(viewerRoles) && (
              <p className="text-xs text-content-secondary mt-1">
                (Admin: tu peux créer uniquement des comptes sales/ops)
              </p>
            )}
          </div>

          <form className="p-6 grid gap-6 md:grid-cols-2" onSubmit={handleCreateUser}>
            <div className="flex flex-col">
              <label className="text-sm font-medium text-content-primary mb-2">Identifiant *</label>
              <input
                className="border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent pilot-input"
                value={newUsername}
                onChange={e => setNewUsername(e.target.value)}
                placeholder="nom.utilisateur"
                required
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-content-primary mb-2">Nom complet</label>
              <input
                className="border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent pilot-input"
                value={newFullName}
                onChange={e => setNewFullName(e.target.value)}
                placeholder="Prénom Nom"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-content-primary mb-2">Email</label>
              <input
                className="border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent pilot-input"
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                placeholder="email@exemple.com"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-content-primary mb-2">Mot de passe *</label>
              <input
                className="border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent pilot-input"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Minimum 8 caractères"
                required
              />
              {passwordStrength && (
                <div className={`text-xs mt-1 ${passwordStrength.color}`}>
                  Force: {passwordStrength.text}
                </div>
              )}
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-content-primary mb-2">
                Confirmer le mot de passe *
              </label>
              <input
                className={`border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent ${
                  confirmPassword && newPassword !== confirmPassword
                    ? 'border-red-300'
                    : 'border-border'
                }`}
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Retapez le mot de passe"
                required
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <div className="text-xs text-red-600 mt-1">Les mots de passe ne correspondent pas</div>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-medium text-content-primary mb-3 block">Rôles *</label>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-slate-700 focus:ring-slate-300 border-border rounded"
                    checked={roleSales}
                    onChange={e => setRoleSales(e.target.checked)}
                  />
                  <span className="text-sm text-content-primary">Ventes (sales)</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-slate-700 focus:ring-slate-300 border-border rounded"
                    checked={roleOps}
                    onChange={e => setRoleOps(e.target.checked)}
                  />
                  <span className="text-sm text-content-primary">Ops</span>
                </label>

                {allowCreateAuditor && (
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-slate-700 focus:ring-slate-300 border-border rounded"
                      checked={roleAuditor}
                      onChange={e => setRoleAuditor(e.target.checked)}
                    />
                    <span className="text-sm text-content-primary">Auditeur (auditor)</span>
                  </label>
                )}

                {allowCreateAdmin && (
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-slate-700 focus:ring-slate-300 border-border rounded"
                      checked={roleAdmin}
                      onChange={e => setRoleAdmin(e.target.checked)}
                    />
                    <span className="text-sm text-content-primary">Admin</span>
                  </label>
                )}
              </div>
            </div>

            <div className="md:col-span-2 flex justify-end pt-4 border-t">
              <button
                type="submit"
                className="bg-slate-900 hover:bg-slate-950 text-white font-medium px-6 py-2.5 rounded-lg shadow-sm transition-colors"
              >
                Créer l'utilisateur
              </button>
            </div>
          </form>
        </section>

        {/* MODAL ROLES */}
        {rolesModalOpen && rolesTarget && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
            <div className="bg-surface-card rounded-xl shadow-xl w-full max-w-lg border">
              <div className="p-5 border-b flex items-start justify-between">
                <div>
                  <div className="text-lg font-semibold text-content-primary">Modifier rôles</div>
                  <div className="text-sm text-content-secondary mt-1">
                    Utilisateur: <b>{rolesTarget.username}</b>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setRolesModalOpen(false)
                    setRolesTarget(null)
                    setRolesDraft([])
                  }}
                  className="text-sm text-content-secondary hover:text-content-primary"
                  type="button"
                >
                  ✕
                </button>
              </div>

              <div className="p-5 space-y-3">
                <div className="text-xs text-content-secondary">
                  Règle: admin peut assigner uniquement sales/ops. Auditor & admin uniquement par superadmin.
                </div>

                <div className="flex flex-wrap gap-3">
                  {ALL_ROLES.filter(r => canSeeRole(viewerRoles, r) && canAssignRole(viewerRoles, r)).map(role => (
                    <label
                      key={role}
                      className="flex items-center gap-2 border rounded-lg px-3 py-2"
                    >
                      <input
                        type="checkbox"
                        checked={rolesDraft.includes(role)}
                        onChange={() => toggleRoleDraft(role)}
                      />
                      <span className="text-sm">{role}</span>
                    </label>
                  ))}
                </div>

                <div className="flex justify-end gap-2 pt-3">
                  <button
                    disabled={rolesSaving}
                    onClick={() => {
                      setRolesModalOpen(false)
                      setRolesTarget(null)
                      setRolesDraft([])
                    }}
                    className="px-4 py-2 rounded-lg border text-sm hover:bg-surface-primary disabled:opacity-60"
                    type="button"
                  >
                    Annuler
                  </button>
                  <button
                    disabled={rolesSaving}
                    onClick={saveRoles}
                    className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-950 text-white text-sm disabled:opacity-60"
                    type="button"
                  >
                    {rolesSaving ? 'Enregistrement...' : 'Enregistrer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  function renderInvoiceTab() {
    return (
      <div className="space-y-6">
        {/* MODEL INTEGRE */}
        <section className="bg-surface-card rounded-lg shadow-sm border">
          <div className="p-6 border-b flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-content-primary">Modèle de facture intégré</h2>
              <p className="text-sm text-content-secondary">Configure les infos affichées sur les factures.</p>
            </div>
            {tplSaving && <div className="text-xs text-slate-700 font-medium">Sauvegarde...</div>}
          </div>

          {tplLoading ? (
            <div className="p-8 text-center text-content-secondary">Chargement du modèle...</div>
          ) : (
            <form className="p-6 grid gap-6 md:grid-cols-2" onSubmit={handleSaveTemplate}>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-content-primary mb-2">Nom société</label>
                <input
                  className="border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent pilot-input"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  placeholder="Fedis SARL"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-content-primary mb-2">Contacts</label>
                <input
                  className="border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent pilot-input"
                  value={companyContacts}
                  onChange={e => setCompanyContacts(e.target.value)}
                  placeholder="Téléphone / Email / WhatsApp"
                />
              </div>

              <div className="flex flex-col md:col-span-2">
                <label className="text-sm font-medium text-content-primary mb-2">Adresse</label>
                <textarea
                  className="border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent min-h-[80px] pilot-input"
                  value={companyAddress}
                  onChange={e => setCompanyAddress(e.target.value)}
                  placeholder="Adresse complète"
                />
              </div>

              <div className="flex flex-col md:col-span-2">
                <label className="text-sm font-medium text-content-primary mb-2">Infos légales</label>
                <textarea
                  className="border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent min-h-[70px] pilot-input"
                  value={companyLegal}
                  onChange={e => setCompanyLegal(e.target.value)}
                  placeholder="RCCM, IFU, etc."
                />
              </div>

              <div className="flex flex-col md:col-span-2">
                <label className="text-sm font-medium text-content-primary mb-2">Note de bas de page</label>
                <textarea
                  className="border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent min-h-[70px] pilot-input"
                  value={footerNote}
                  onChange={e => setFooterNote(e.target.value)}
                  placeholder="Ex: Merci pour votre confiance..."
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-content-primary mb-2">Couleur accent</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    className="h-10 w-14 border border-border rounded"
                    value={accentColor}
                    onChange={e => setAccentColor(e.target.value)}
                    title="accent_color"
                  />
                  <input
                    className="border border-border rounded-lg px-3 py-2.5 w-full focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent pilot-input"
                    value={accentColor}
                    onChange={e => setAccentColor(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-content-primary mb-2">Format page</label>
                <select
                  className="border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent pilot-input"
                  value={pageFormat}
                  onChange={e => setPageFormat(e.target.value)}
                >
                  <option value="A4">A4</option>
                  <option value="A5">A5</option>
                  <option value="LETTER">LETTER</option>
                </select>
              </div>

              <div className="md:col-span-2 flex items-center justify-between border rounded-lg p-4 bg-surface-primary">
                <div>
                  <div className="text-sm font-medium text-content-primary">Afficher le logo</div>
                  <div className="text-xs text-content-secondary">
                    {logoPath ? `Logo: ${logoPath}` : 'Aucun logo upload'}
                  </div>
                </div>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={showLogo}
                    onChange={e => setShowLogo(e.target.checked)}
                  />
                  <span className="text-sm text-content-primary">Actif</span>
                </label>
              </div>

              <div className="md:col-span-2 border rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-content-primary">Logo</div>
                    <div className="text-xs text-content-secondary">Upload un fichier image (png/jpg).</div>
                  </div>
                  <input type="file" accept="image/*" onChange={handleUploadLogo} />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-medium text-content-primary mb-2 block">
                  custom_field_map (JSON)
                </label>
                <textarea
                  className="border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent w-full min-h-[160px] font-mono text-xs pilot-input"
                  value={customFieldMapText}
                  onChange={e => setCustomFieldMapText(e.target.value)}
                  placeholder='{"customer_name":"customer.full_name"}'
                />
                <div className="text-xs text-content-secondary mt-1">Doit être un JSON valide.</div>
              </div>

              <div className="md:col-span-2 flex justify-end pt-4 border-t">
                <button
                  type="submit"
                  className="bg-slate-900 hover:bg-slate-950 text-white font-medium px-6 py-2.5 rounded-lg shadow-sm transition-colors"
                >
                  Enregistrer
                </button>
              </div>
            </form>
          )}
        </section>

        {/* TEMPLATE PERSONNALISE */}
        <section className="bg-surface-card rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-content-primary">Modèle personnalisé</h2>
            <p className="text-sm text-content-secondary mt-1">
              Option avancée: importer un template (ex: docx) et l’activer/désactiver.
            </p>
          </div>

          <div className="p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border rounded-lg p-4 bg-surface-primary">
              <div>
                <div className="text-sm font-medium text-content-primary">Statut</div>
                <div className="text-xs text-content-secondary">
                  {useCustomTemplate
                    ? `Activé (${customTemplateType || 'type inconnu'})`
                    : 'Désactivé'}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleToggleCustomTemplate(true)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    useCustomTemplate
                      ? 'bg-green-100 text-green-800 border border-green-200'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  Activer
                </button>
                <button
                  type="button"
                  onClick={() => handleToggleCustomTemplate(false)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    !useCustomTemplate
                      ? 'bg-surface-primary text-content-primary border border-border'
                      : 'bg-gray-900 hover:bg-gray-800 text-white'
                  }`}
                >
                  Désactiver
                </button>
              </div>
            </div>

            <div className="border rounded-lg p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-content-primary">Importer un modèle</div>
                  <div className="text-xs text-content-secondary">
                    Le custom_field_map sera envoyé avec le fichier.
                  </div>
                </div>
                <input type="file" onChange={handleUploadCustomTemplate} />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleDeleteCustomTemplate}
                className="px-4 py-2 rounded-lg bg-red-700 hover:bg-red-800 text-white text-sm"
              >
                Supprimer / Désactiver définitivement
              </button>
            </div>
          </div>
        </section>
      </div>
    )
  }

  // ======================================================
  // ================== RENDER PRINCIPAL ==================
  // ======================================================

  return (
    <div className="p-6 space-y-8">
      <header className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Parametres</h1>
          <p className="text-sm text-slate-500">Gestion des acces et configuration facture.</p>
        </div>

        <div className="flex flex-wrap gap-2 text-sm font-semibold">
          <button
            className={`pilot-tab ${tab === 'general' ? 'pilot-tab-active' : 'pilot-tab-inactive'}`}
            onClick={() => setTab('general')}
            type="button"
          >
            General
          </button>

          <button
            className={`pilot-tab ${tab === 'users' ? 'pilot-tab-active' : 'pilot-tab-inactive'}`}
            onClick={() => setTab('users')}
            type="button"
          >
            Utilisateurs
          </button>

          <button
            className={`pilot-tab ${tab === 'invoice' ? 'pilot-tab-active' : 'pilot-tab-inactive'}`}
            onClick={() => setTab('invoice')}
            type="button"
          >
            Format facture
          </button>

          <button
            className={`pilot-tab ${tab === 'modules' ? 'pilot-tab-active' : 'pilot-tab-inactive'}`}
            onClick={() => setTab('modules')}
            type="button"
          >
            Modules
          </button>

          <button
            className={`pilot-tab ${tab === 'email' ? 'pilot-tab-active' : 'pilot-tab-inactive'}`}
            onClick={() => setTab('email')}
            type="button"
          >
            Email
          </button>

          <button
            className={`pilot-tab ${tab === 'automations' ? 'pilot-tab-active' : 'pilot-tab-inactive'}`}
            onClick={() => setTab('automations')}
            type="button"
          >
            Automatisations
          </button>

          <button
            className={`pilot-tab ${tab === 'security' ? 'pilot-tab-active' : 'pilot-tab-inactive'}`}
            onClick={() => setTab('security')}
            type="button"
          >
            Sécurité
          </button>

          <button
            className={`pilot-tab ${tab === 'loyalty' ? 'pilot-tab-active' : 'pilot-tab-inactive'}`}
            onClick={() => setTab('loyalty')}
            type="button"
          >
            🏅 Fidélité
          </button>

          <button
            className={`pilot-tab ${tab === 'hr' ? 'pilot-tab-active' : 'pilot-tab-inactive'}`}
            onClick={() => setTab('hr')}
            type="button"
          >
            👥 RH
          </button>

        </div>
      </header>

      {flash && (
        <div
          className={`p-4 rounded-lg border ${
            flash.includes('❌')
              ? 'bg-red-50 border-red-200 text-red-700'
              : flash.includes('ℹ️')
                ? 'bg-surface-primary border-border text-content-primary'
                : 'bg-green-50 border-green-200 text-green-700'
          }`}
        >
          {flash}
        </div>
      )}

      {tab === 'general' && <SettingsGeneral onFlash={setFlash} />}
      {tab === 'users' && renderUsersTab()}
      {tab === 'invoice' && <SettingsInvoice setFlash={setFlash} />}
      {tab === 'modules' && <SettingsModules onFlash={setFlash} />}
      {tab === 'email' && <SettingsEmail onFlash={setFlash} />}
      {tab === 'automations' && <SettingsAutomations onFlash={setFlash} />}
      {tab === 'security' && <SettingsSecurity onFlash={setFlash} />}
      {tab === 'loyalty' && <SettingsLoyalty onFlash={setFlash} />}
      {tab === 'hr' && <SettingsHR onFlash={setFlash} />}
    </div>
  )
}
